import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import useFetch from 'use-http';
import _ from 'lodash';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useUserState } from '@/components/context/UserContext';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { MaterialIcon } from '@/components/ui/shared/materialIcon';
import { appendPeriodIfMissing } from '@/v2/utils/stringUtils';

const emergencyContactConsentExtId =
  process.env.REACT_APP_AGREEMENTS_EMERGENCY_CONCACT_CONSENT_EXTID;
const emergencyContactPersonExtId =
  process.env.REACT_APP_AGREEMENTS_EMERGENCY_CONCACT_PERSON_EXTID;

const agreementSchema = z
  .record(z.string(), z.record(z.string(), z.union([z.boolean(), z.string()])))
  .superRefine((formData, ctx) => {
    const isConsentChecked = Object.values(formData).some(
      (group) => group[emergencyContactConsentExtId] === true,
    );

    if (isConsentChecked) {
      const entryWithEmergencyContactName = Object.entries(formData).find(
        ([, group]) => group[emergencyContactPersonExtId] !== undefined,
      );
      if (
        !entryWithEmergencyContactName ||
        entryWithEmergencyContactName[1][emergencyContactPersonExtId] === ''
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Šis laukas yra privalomas',
          path: [
            entryWithEmergencyContactName?.[0] ?? '?',
            emergencyContactPersonExtId,
          ],
        });
      }
    }
  });

const ManageAgreementsForm = () => {
  const history = useHistory();
  const { t } = useTranslation();
  const [authorizedPersonConsentId, setAuthorizedPersonConsentId] = useState();
  const [authorizedPersonsVisible, setAuthorizedPersonsVisible] =
    useState(true);
  const [consentsVisible, setConsentsVisible] = useState(true);
  const [initialValues, setInitialValues] = useState({});
  const [currentData, setCurrentData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const agreementId = process.env.REACT_APP_AGREEMENTID;
  const { get: getAgreementsRequest, response: getAgreementsRequestResponse } =
    useFetch(`/UserAccount/Agreements`);
  const { post: postAgreementsRequest, response: postAgreementsResponse } =
    useFetch(`/UserAccount/UpdateAgreement`);
  const [
    isAuthorizedPersonsUpdateSuccess,
    setIsAuthorizedPersonsUpdateSuccess,
  ] = useState(history.location.state?.success);
  const [AMISErrorMessage, setAMISErrorMessage] = useState(
    history.location.state?.errorMessage,
  );
  const [
    isAuthorizedPersonsUpdateSuccessModalVisible,
    setIsAuthorizedPersonsUpdateSuccessModalVisible,
  ] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [AuthorizedPersons, setAuthorizedPersons] = useState([]);
  const [authorizedPersonsPassId, setAuthorizedPersonsPassId] = useState({});
  const userState = useUserState();
  const [loading, setLoading] = useState(false);
  const [stringConsentIds, setStringConsentIds] = useState();

  const form = useForm({
    resolver: zodResolver(agreementSchema),
    defaultValues: {},
    mode: 'onSubmit',
  });

  useEffect(() => {
    if (Object.keys(initialValues).length > 0) {
      form.reset(initialValues);
    }
  }, [initialValues]);

  const handleSubmit = async (data) => {
    setLoading(true);
    let changes = [];
    currentData.forEach((x, idx) => {
      if (data[idx]?.[x.consentId] !== undefined) {
        const value = data[idx][x.consentId];
        if (x.value !== value) {
          x.value = value;
          changes.push(x);
        }
      }
    });

    let objTrimmedChangesArray = changes.map((consent) => ({
      medConsultPersonGuid: userState.id,
      patientConsentId: consent.id,
      value: consent.value,
      consentId: consent.consentId,
      espbiEsi: userState.espbiEsi,
    }));

    const response = await postAgreementsRequest(' ', objTrimmedChangesArray);
    if (
      postAgreementsResponse.status >= 200 &&
      postAgreementsResponse.status < 300
    ) {
      if (response.error) {
        setModalMessage(`Įvyko klaida: ${response.error}`);
      } else {
        setModalMessage('Duomenys pakeisti sėkmingai');
        setRefresh((prev) => !prev);
      }
    } else {
      setModalMessage('Įvyko klaida: Negalime atnaujinti sutikimo.');
    }
    setLoading(false);
    setIsAuthorizedPersonsUpdateSuccessModalVisible(true);
  };

  useEffect(() => {
    if (isAuthorizedPersonsUpdateSuccess !== undefined) {
      setModalMessage(
        isAuthorizedPersonsUpdateSuccess
          ? 'Duomenys pakeisti sėkmingai'
          : `Įvyko klaida: ${AMISErrorMessage}`,
      );
      history.replace({ state: {} });
      setIsAuthorizedPersonsUpdateSuccessModalVisible(true);
    }
  }, []);

  useEffect(() => {
    (async function () {
      setLoading(true);
      await getAgreementsRequest(
        userState.espbiEsi ? `?espbiEsi=${userState.espbiEsi}` : '',
      );

      if (getAgreementsRequestResponse.status !== 200) {
        setConsentsVisible(false);
        setLoading(false);
        return;
      }

      const response = await getAgreementsRequestResponse;

      if (!response?.data?.consents?.length) {
        setConsentsVisible(false);
        setLoading(false);
        return;
      }

      const consents = _.orderBy(
        response.data.consents.filter(
          (arr) =>
            arr.valueType === 'Boolean' ||
            (arr.valueType === 'String' && arr.amisConsentId != agreementId),
        ),
        ['quequenumber', 'name'],
        ['asc'],
      );

      const initialValuesObject = {};
      const stringIds = [];

      consents.forEach((x, idx) => {
        if (x.valueType === 'String' && x.consentId !== agreementId) {
          stringIds.push(x.consentId);
        }
        initialValuesObject[idx] = {
          [x.consentId]: x.value ?? (x.valueType === 'Boolean' ? false : ''),
        };
      });

      setStringConsentIds(stringIds);
      setInitialValues(initialValuesObject);
      setCurrentData(consents);

      const authorized = response.data.consents.filter(
        (arr) => arr.valueType === 'String' && arr.amisConsentId == agreementId,
      );

      if (!authorized.length) setAuthorizedPersonsVisible(false);

      setAuthorizedPersons(
        authorized
          .map((item) => item.value)
          .filter(Boolean)
          .join(', '),
      );

      if (authorized.length > 0) {
        setAuthorizedPersonsPassId(authorized[0].id);
        setAuthorizedPersonConsentId(authorized[0].consentId);
      }

      setLoading(false);
    })();
  }, [refresh]);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold">
              {t('userAccount.userInfoTab.agreementsTitle')}
            </h2>
            <div className="text-muted-foreground text-sm">
              Duomenys apie sutikimus
            </div>
          </div>
          <div className="hidden sm:block">
            <Button type="submit" form="agreements-form">
              {t('userAccount.userInfoTab.updateData')}
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <Form {...form}>
            <form
              id="agreements-form"
              onSubmit={form.handleSubmit(handleSubmit)}
              className="space-y-4"
            >
              {!consentsVisible && (
                <div>
                  <h3 className="text-sm text-muted-foreground">
                    Nėra sutikimų
                  </h3>
                </div>
              )}

              {consentsVisible && (
                <>
                  {currentData.map((ag, idx) => (
                    <div key={idx} className="space-y-2">
                      {ag.valueType === 'Boolean' && (
                        <FormField
                          control={form.control}
                          name={`${idx}.${ag.consentId}`}
                          render={({ field }) => (
                            <FormItem className="flex items-center gap-3">
                              <FormControl>
                                <Checkbox
                                  checked={field.value}
                                  onCheckedChange={field.onChange}
                                />
                              </FormControl>
                              <FormLabel className="text-base font-normal leading-tight">
                                {appendPeriodIfMissing(
                                  `${ag.name || ''} ${
                                    field.value && ag.validTo
                                      ? `(Galioja iki ${ag.validTo.substring(
                                          0,
                                          10,
                                        )})`
                                      : ''
                                  }`,
                                )}
                              </FormLabel>
                            </FormItem>
                          )}
                        />
                      )}
                      {ag.valueType === 'String' && (
                        <FormField
                          control={form.control}
                          name={`${idx}.${ag.consentId}`}
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>
                                {appendPeriodIfMissing(ag.name)}
                              </FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  className="max-w-[450px] text-muted-foreground"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>
                  ))}

                  {authorizedPersonsVisible && (
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="font-medium">Įgalioti asmenys</span>
                        <Button
                          type="button"
                          className={'w-auto'}
                          variant="light"
                          onClick={() =>
                            history.push(
                              '/userAccount/personal-info/manage-authorized-persons',
                              {
                                data: {
                                  id: authorizedPersonsPassId,
                                  value: AuthorizedPersons,
                                  consentId: authorizedPersonConsentId,
                                },
                              },
                            )
                          }
                        >
                          <MaterialIcon icon="edit" size={16} />
                          Keisti įgaliotus asmenis
                        </Button>
                      </div>
                      <p className="max-w-[900px] text-muted-foreground">
                        {AuthorizedPersons.length !== 0
                          ? AuthorizedPersons
                          : 'Nenurodyta'}
                      </p>
                    </div>
                  )}
                </>
              )}

              <div className="block sm:hidden pb-3">
                <Button className="w-full" type="submit" loading={loading}>
                  {t('userAccount.userInfoTab.updateData')}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Dialog
        open={isAuthorizedPersonsUpdateSuccessModalVisible}
        onOpenChange={() =>
          setIsAuthorizedPersonsUpdateSuccessModalVisible(false)
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{modalMessage}</DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant={'secondary'}
              onClick={() =>
                setIsAuthorizedPersonsUpdateSuccessModalVisible(false)
              }
            >
              Gerai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageAgreementsForm;
